import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import authgear from "../authgear";
import Header from "../components/Header";

const base_url = process.env.NODE_ENV === "production" ? "https://hooks-28xd.onrender.com" : "http://localhost:4000";

const BlueskySetup = () => {
    const navigate = useNavigate();
    const [connected, setConnected] = useState(null);
    const [handle, setHandle] = useState(null);
    const [status, setStatus] = useState("");
    const [loading, setLoading] = useState({ status: false, post: false, queue: false, queueAdd: false, replyTarget: false, replyTargetRefresh: false, replySubmit: false });
    const [text, setText] = useState("");
    const [mediaUrl, setMediaUrl] = useState("");
    const [altText, setAltText] = useState("");
    const [lastPost, setLastPost] = useState(null);
    const [queuedText, setQueuedText] = useState("");
    const [queue, setQueue] = useState([]);
    const [replyTarget, setReplyTarget] = useState(null);
    const [replyText, setReplyText] = useState("");

    const authHeaders = useCallback(async () => {
        await authgear.refreshAccessTokenIfNeeded();
        return { Authorization: `Bearer ${authgear.accessToken}` };
    }, []);

    const loadStatus = useCallback(async () => {
        setLoading(l => ({ ...l, status: true }));
        try {
            const headers = await authHeaders();
            const { data } = await axios.get(`${base_url}/bluesky/status`, { headers });
            setConnected(data.connected);
            setHandle(data.handle || null);
            if (!data.connected && data.message) setStatus(data.message);
        } catch (e) {
            setStatus("Failed to load Bluesky connection status.");
        } finally {
            setLoading(l => ({ ...l, status: false }));
        }
    }, [authHeaders]);

    const loadQueue = useCallback(async () => {
        setLoading(l => ({ ...l, queue: true }));
        try {
            const headers = await authHeaders();
            const { data } = await axios.get(`${base_url}/bluesky/queue`, { headers });
            setQueue(data);
        } catch (e) {
            setStatus("Failed to load message queue.");
        } finally {
            setLoading(l => ({ ...l, queue: false }));
        }
    }, [authHeaders]);

    const loadReplyTarget = useCallback(async () => {
        setLoading(l => ({ ...l, replyTarget: true }));
        try {
            const headers = await authHeaders();
            const { data } = await axios.get(`${base_url}/bluesky/reply-target`, { headers });
            setReplyTarget(data);
            setReplyText("");
        } catch (e) {
            setStatus("Failed to load today's reply target.");
        } finally {
            setLoading(l => ({ ...l, replyTarget: false }));
        }
    }, [authHeaders]);

    useEffect(() => {
        window.scrollTo(0, 0);
        if (authgear.sessionState !== "AUTHENTICATED") {
            navigate("/login");
            return;
        }
        loadStatus();
        loadQueue();
        loadReplyTarget();
    }, [navigate, loadStatus, loadQueue, loadReplyTarget]);

    const handlePost = async (e) => {
        e.preventDefault();
        if (!text.trim() && !mediaUrl.trim()) return;
        setLoading(l => ({ ...l, post: true }));
        setStatus("");
        setLastPost(null);
        try {
            const headers = await authHeaders();
            const { data } = await axios.post(
                `${base_url}/bluesky/post`,
                { text: text.trim(), mediaUrl: mediaUrl.trim() || undefined, altText: altText.trim() || undefined },
                { headers }
            );
            if (data.success) {
                setStatus("Posted to Bluesky successfully!");
                setLastPost(data);
                setText("");
                setMediaUrl("");
                setAltText("");
            } else {
                setStatus(`Failed to post: ${data.message}`);
            }
        } catch (e) {
            setStatus(`Error posting: ${e.response?.data?.message || e.message}`);
        } finally {
            setLoading(l => ({ ...l, post: false }));
        }
    };

    const handleQueueAdd = async (e) => {
        e.preventDefault();
        if (!queuedText.trim()) return;
        setLoading(l => ({ ...l, queueAdd: true }));
        setStatus("");
        try {
            const headers = await authHeaders();
            await axios.post(`${base_url}/bluesky/queue`, { text: queuedText.trim() }, { headers });
            setQueuedText("");
            await loadQueue();
        } catch (e) {
            setStatus(`Error queueing message: ${e.response?.data?.message || e.message}`);
        } finally {
            setLoading(l => ({ ...l, queueAdd: false }));
        }
    };

    const handleQueueDelete = async (id) => {
        try {
            const headers = await authHeaders();
            await axios.delete(`${base_url}/bluesky/queue/${id}`, { headers });
            await loadQueue();
        } catch (e) {
            setStatus(`Error removing queued message: ${e.response?.data?.message || e.message}`);
        }
    };

    const handleQueueMove = async (index, direction) => {
        const swapWith = index + direction;
        if (swapWith < 0 || swapWith >= queue.length) return;

        const reordered = [...queue];
        [reordered[index], reordered[swapWith]] = [reordered[swapWith], reordered[index]];
        setQueue(reordered);

        try {
            const headers = await authHeaders();
            await axios.put(`${base_url}/bluesky/queue/reorder`, { ids: reordered.map(m => m._id) }, { headers });
        } catch (e) {
            setStatus(`Error reordering queue: ${e.response?.data?.message || e.message}`);
            await loadQueue();
        }
    };

    const handleReplyTargetRefresh = async () => {
        setLoading(l => ({ ...l, replyTargetRefresh: true }));
        setStatus("");
        try {
            const headers = await authHeaders();
            const { data } = await axios.post(`${base_url}/bluesky/reply-target/refresh`, {}, { headers });
            setReplyTarget(data);
            setReplyText("");
        } catch (e) {
            setStatus(`Error finding a reply target: ${e.response?.data?.message || e.message}`);
        } finally {
            setLoading(l => ({ ...l, replyTargetRefresh: false }));
        }
    };

    const handlePostReply = async (e) => {
        e.preventDefault();
        if (!replyText.trim()) return;
        setLoading(l => ({ ...l, replySubmit: true }));
        setStatus("");
        try {
            const headers = await authHeaders();
            const { data } = await axios.post(`${base_url}/bluesky/reply-target/reply`, { text: replyText.trim() }, { headers });
            setStatus("Reply posted to Bluesky!");
            setReplyTarget(data.target);
        } catch (e) {
            setStatus(`Error posting reply: ${e.response?.data?.message || e.message}`);
        } finally {
            setLoading(l => ({ ...l, replySubmit: false }));
        }
    };

    const cardStyle = {
        backgroundColor: "#1e1e3a",
        border: "1px solid #333",
        borderRadius: "8px",
        padding: "20px",
        marginBottom: "20px"
    };

    const btnStyle = (color = "#00d4ff", disabled = false) => ({
        backgroundColor: disabled ? "#444" : color,
        color: "#fff",
        border: "none",
        padding: "8px 18px",
        borderRadius: "4px",
        cursor: disabled ? "not-allowed" : "pointer",
        fontSize: "14px",
        opacity: disabled ? 0.6 : 1,
        marginRight: "8px"
    });

    const inputStyle = {
        width: "100%",
        padding: "8px",
        borderRadius: "4px",
        border: "1px solid #555",
        background: "#111",
        color: "#ddd",
        fontSize: "13px",
        boxSizing: "border-box",
        marginBottom: "10px"
    };

    const isSuccess = status.includes("success") || status.includes("Posted");

    return (
        <div style={{ minHeight: "100vh", background: "#0d0d1a", color: "#ddd", fontFamily: "Verdana, sans-serif" }}>
            <Header />
            <div style={{ padding: "24px" }}>
                <h2 style={{ color: "#00d4ff", marginBottom: "24px" }}>Bluesky Setup</h2>

                {status && (
                    <div style={{ ...cardStyle, backgroundColor: isSuccess ? "#1a2a1a" : "#2a1a1a", borderColor: isSuccess ? "#2a4a2a" : "#4a2a2a", color: isSuccess ? "#8f8" : "#f88", marginBottom: "16px" }}>
                        {status}
                    </div>
                )}

                <div style={cardStyle}>
                    <h3 style={{ color: "#00d4ff", marginTop: 0 }}>Bluesky Account</h3>
                    {loading.status ? (
                        <p style={{ color: "#aaa" }}>Checking connection...</p>
                    ) : connected ? (
                        <p style={{ color: "#8f8", fontSize: "14px" }}>Connected as @{handle}</p>
                    ) : (
                        <p style={{ color: "#f88", fontSize: "14px" }}>
                            Not connected. Check that BLUE_SKY_TAG and BLUE_SKY_APP_PASS are set correctly in the backend .env.
                        </p>
                    )}
                </div>

                {connected && (
                    <div style={cardStyle}>
                        <h3 style={{ color: "#00d4ff", marginTop: 0 }}>Post</h3>
                        <form onSubmit={handlePost}>
                            <textarea
                                value={text}
                                onChange={e => setText(e.target.value)}
                                placeholder="What's happening?"
                                rows={4}
                                style={{ ...inputStyle, resize: "vertical" }}
                            />
                            <input
                                type="text"
                                value={mediaUrl}
                                onChange={e => setMediaUrl(e.target.value)}
                                placeholder="Image or video URL (optional)"
                                style={inputStyle}
                            />
                            {mediaUrl.trim() && (
                                <input
                                    type="text"
                                    value={altText}
                                    onChange={e => setAltText(e.target.value)}
                                    placeholder="Alt text (optional)"
                                    style={inputStyle}
                                />
                            )}
                            <button type="submit" style={btnStyle("#0085ff", loading.post)} disabled={loading.post || (!text.trim() && !mediaUrl.trim())}>
                                {loading.post ? "Posting..." : "Post to Bluesky"}
                            </button>
                        </form>

                        {lastPost && (
                            <div style={{ marginTop: "16px", fontSize: "12px", color: "#888", wordBreak: "break-all" }}>
                                {lastPost.uri}
                            </div>
                        )}
                    </div>
                )}

                {connected && (
                    <div style={cardStyle}>
                        <h3 style={{ color: "#00d4ff", marginTop: 0 }}>Message Queue</h3>
                        <p style={{ color: "#888", fontSize: "12px", marginTop: 0 }}>
                            One queued text message and one queued photo/video go out per day, combined into their own post separate from the daily training update.
                        </p>
                        <form onSubmit={handleQueueAdd}>
                            <textarea
                                value={queuedText}
                                onChange={e => setQueuedText(e.target.value)}
                                placeholder="Message to queue..."
                                rows={2}
                                style={{ ...inputStyle, resize: "vertical" }}
                            />
                            <button type="submit" style={btnStyle("#0085ff", loading.queueAdd)} disabled={loading.queueAdd || !queuedText.trim()}>
                                {loading.queueAdd ? "Queueing..." : "Add to Queue"}
                            </button>
                        </form>

                        <div style={{ marginTop: "16px" }}>
                            {loading.queue ? (
                                <p style={{ color: "#aaa", fontSize: "13px" }}>Loading queue...</p>
                            ) : queue.length === 0 ? (
                                <p style={{ color: "#888", fontSize: "13px" }}>No messages queued.</p>
                            ) : (
                                queue.map((m, i) => (
                                    <div key={m._id} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "8px 0", borderTop: i === 0 ? "none" : "1px solid #333" }}>
                                        <div style={{ fontSize: "13px", color: "#ddd", flex: 1, marginRight: "12px" }}>
                                            {i === 0 && <span style={{ color: "#00d4ff", fontSize: "11px", display: "block" }}>Next up</span>}
                                            {m.mediaUrl && (
                                                /\.(mp4|mov|webm)$/i.test(m.mediaUrl) ? (
                                                    <video src={m.mediaUrl} controls muted style={{ maxWidth: "160px", borderRadius: "4px", display: "block", marginBottom: m.text ? "6px" : 0 }} />
                                                ) : (
                                                    <img src={m.mediaUrl} alt="Queued" style={{ maxWidth: "160px", borderRadius: "4px", display: "block", marginBottom: m.text ? "6px" : 0 }} />
                                                )
                                            )}
                                            {m.text}
                                        </div>
                                        <div style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                                            <button
                                                onClick={() => handleQueueMove(i, -1)}
                                                disabled={i === 0}
                                                style={{ ...btnStyle("#555", i === 0), padding: "4px 8px" }}
                                                title="Move up"
                                            >
                                                ↑
                                            </button>
                                            <button
                                                onClick={() => handleQueueMove(i, 1)}
                                                disabled={i === queue.length - 1}
                                                style={{ ...btnStyle("#555", i === queue.length - 1), padding: "4px 8px" }}
                                                title="Move down"
                                            >
                                                ↓
                                            </button>
                                            <button onClick={() => handleQueueDelete(m._id)} style={{ ...btnStyle("#aa3333"), marginRight: 0, padding: "4px 10px" }}>
                                                Remove
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}

                {connected && (
                    <div style={cardStyle}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <h3 style={{ color: "#00d4ff", marginTop: 0 }}>Today's Reply Target</h3>
                            <button
                                onClick={handleReplyTargetRefresh}
                                style={{ ...btnStyle("#555", loading.replyTargetRefresh), marginRight: 0, padding: "4px 10px", fontSize: "12px" }}
                                disabled={loading.replyTargetRefresh}
                            >
                                {loading.replyTargetRefresh ? "Searching..." : replyTarget ? "Find Another" : "Search Now"}
                            </button>
                        </div>
                        <p style={{ color: "#888", fontSize: "12px", marginTop: 0 }}>
                            A high-engagement triathlon post is found once a day for you to consider replying to.
                        </p>

                        {loading.replyTarget ? (
                            <p style={{ color: "#aaa", fontSize: "13px" }}>Loading...</p>
                        ) : !replyTarget ? (
                            <p style={{ color: "#888", fontSize: "13px" }}>No target found yet today. Try searching now.</p>
                        ) : (
                            <div>
                                <div style={{ background: "#111", border: "1px solid #333", borderRadius: "6px", padding: "12px", marginBottom: "12px" }}>
                                    <div style={{ fontSize: "12px", color: "#00d4ff", marginBottom: "4px" }}>
                                        @{replyTarget.authorHandle}{replyTarget.authorDisplayName ? ` (${replyTarget.authorDisplayName})` : ""}
                                    </div>
                                    <div style={{ fontSize: "13px", color: "#ddd", marginBottom: "8px" }}>{replyTarget.text}</div>
                                    <div style={{ fontSize: "11px", color: "#888" }}>
                                        {replyTarget.likeCount} likes · {replyTarget.repostCount} reposts · {replyTarget.replyCount} replies
                                        {" · "}
                                        <a
                                            href={`https://bsky.app/profile/${replyTarget.authorHandle}/post/${replyTarget.uri.split("/").pop()}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            style={{ color: "#00d4ff" }}
                                        >
                                            View on Bluesky
                                        </a>
                                    </div>
                                </div>

                                {replyTarget.repliedAt ? (
                                    <p style={{ color: "#8f8", fontSize: "13px" }}>
                                        Replied.{" "}
                                        {replyTarget.replyUri && (
                                            <a
                                                href={`https://bsky.app/profile/${replyTarget.authorHandle}/post/${replyTarget.replyUri.split("/").pop()}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                style={{ color: "#00d4ff" }}
                                            >
                                                View reply
                                            </a>
                                        )}
                                    </p>
                                ) : (
                                    <form onSubmit={handlePostReply}>
                                        <textarea
                                            value={replyText}
                                            onChange={e => setReplyText(e.target.value)}
                                            placeholder="Your reply..."
                                            rows={3}
                                            style={{ ...inputStyle, resize: "vertical" }}
                                        />
                                        <button type="submit" style={btnStyle("#0085ff", loading.replySubmit)} disabled={loading.replySubmit || !replyText.trim()}>
                                            {loading.replySubmit ? "Posting..." : "Post Reply"}
                                        </button>
                                    </form>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default BlueskySetup;
